import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Bell, Users, Loader2, Calendar } from "lucide-react";
import { useGetAdminNotificationByIdQuery, useGetAdminNotificationsQuery } from "@/redux/slices/notificationApi";

export default function NotificationDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: response, isLoading: isLoadingById, isError: isErrorById } = useGetAdminNotificationByIdQuery(id as string, {
    skip: !id,
  });

  const { data: listResponse, isLoading: isLoadingList } = useGetAdminNotificationsQuery({ limit: 1000 }, {
    skip: !id || (!!response?.data && !isErrorById),
  });

  const isLoading = isLoadingById && isLoadingList;

  const notification = response?.data || listResponse?.data?.find((n) => String(n.id) === String(id));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!notification) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold">Notification Not Found</h2>
        <Button onClick={() => navigate('/notifications')} className="mt-4">
          Back to Notifications
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-5xl mx-auto pb-10">
      <Button variant="ghost" className="mb-2" onClick={() => navigate('/notifications')}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Notifications
      </Button>

      <Card className="border shadow-sm">
        <CardHeader className="bg-muted/20 border-b pb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                <Bell className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl mb-2">{notification.title}</CardTitle>
                <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                  <span className="flex items-center">
                    <Calendar className="mr-1 h-4 w-4" />
                    {format(new Date(notification.createdAt), "MMMM d, yyyy 'at' h:mm a")}
                  </span>
                  <span className="flex items-center">
                    <Users className="mr-1 h-4 w-4" />
                    Target: {notification.target === "all" ? "All Students" : "Selected Students"}
                  </span>
                </div>
              </div>
            </div>
            {notification.target === "all" ? (
              <Badge variant="secondary" className="bg-primary/10 text-primary">Broadcast</Badge>
            ) : (
              <Badge variant="outline">Targeted</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-8">
          <div>
            <h3 className="text-lg font-semibold mb-2 text-foreground">Message Body</h3>
            <div className="p-4 bg-muted/20 border rounded-lg whitespace-pre-wrap text-muted-foreground">
              {notification.body}
            </div>
          </div>

          {notification.target === "selected" && (
            <div>
              <h3 className="text-lg font-semibold mb-4 text-foreground flex items-center">
                <Users className="w-5 h-5 mr-2 text-primary" />
                Recipients ({notification.students?.length || 0})
              </h3>
              
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader className="bg-muted/30">
                    <TableRow>
                      <TableHead>Student Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!notification.students || notification.students.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                          No specific recipients found for this notification.
                        </TableCell>
                      </TableRow>
                    ) : (
                      notification.students.map((student) => (
                        <TableRow key={student.id}>
                          <TableCell className="font-medium">{student.name}</TableCell>
                          <TableCell>{student.email}</TableCell>
                          <TableCell>{student.mobile || '-'}</TableCell>
                          <TableCell>
                            <Badge variant={student.status === 'active' ? 'default' : 'secondary'} className={student.status === 'active' ? 'bg-green-500/10 text-green-700 hover:bg-green-500/20' : ''}>
                              {student.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
